<template>
    <div>
        <step-form-heading v-if="!props.promotionId" :icon="faBadgePercent">
            New Promotion

            <template v-slot:description> Create a new promotion.</template>
        </step-form-heading>

        <promotion-details :promotionId="props.promotionId">
            <form-wrapper
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'variable', label: 'Variable' },
                ]"
                :hideNav="!!props.promotionId"
            >
                <vx-alert
                    v-if="success && props.promotionId"
                    data-test="success-alert"
                    color="success"
                    :closeable="false"
                    shadow
                >
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faCircleCheck"></font-awesome-icon>
                    </template>
                    <div class="vx-flex vx-items-center vx-justify-between">
                        <span><strong>Existing Promotion</strong> updated successfully</span>
                        <vx-button href="/settings/integrations/promotions" size="sm" color="success">
                            Back to Promotions
                            <font-awesome-icon :icon="faSquareArrowUpLeft"></font-awesome-icon>
                        </vx-button>
                    </div>
                </vx-alert>

                <vx-alert v-if="error" data-test="error-alert" color="danger" :closeable="false" shadow>
                    {{ error }}
                </vx-alert>

                <vx-expandable-card id="name">
                    <template v-slot:header> Name</template>

                    <template v-slot:content>
                        <vx-input
                            v-model="promotion.name"
                            autofocus
                            size="lg"
                            :error="displayFirstError(validate.name)"
                            :maxlength="80"
                        ></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="variable">
                    <template v-slot:header> Message Variable Code</template>
                    <template v-slot:headerSubtitle>
                        This is the name of the variable you will use in message builder.
                    </template>

                    <template v-slot:content>
                        <vx-input
                            v-model="promotion.code"
                            size="lg"
                            :error="displayFirstError(validate.code)"
                            :maxlength="40"
                        ></vx-input>

                        <vx-alert v-if="!!props.promotionId" class="vx-mt-6" color="info" :closeable="false">
                            If you change this you will need to update Campaigns and Automations that rely on this
                            Message Variable Code manually.
                        </vx-alert>
                    </template>
                </vx-expandable-card>

                <vx-sticky-footer>
                    <vx-button href="/settings/integrations/promotions" class="vx-w-20" size="lg" color="muted">
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="promotion-save"
                        class="vx-grow"
                        size="lg"
                        color="primary"
                        :loading="saving"
                        :disabled="!validate.$anyDirty"
                    >
                        {{ props.promotionId ? 'Update' : 'Save' }}
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </promotion-details>
    </div>
</template>

<script setup>
import { faBadgePercent, faCircleCheck, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import PromotionDetails from '~/components/promotions/PromotionDetails.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxStickyFooter } from '@voxie/frontend-components';
import { usePromotionDetails } from '~/components/promotions/usePromotionDetails';
import { displayFirstError, startAlphanumericDashUnderscore } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { useToasted } from '~/composables/useToasted';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ref } from 'vue';

const toasted = useToasted();

const props = defineProps({
    promotionId: {
        type: Number,
        required: false,
    },
});

const { promotion, save, saving, error, onMountedPromotion } = usePromotionDetails();
const success = ref(false);

const validate = useVuelidate(
    {
        name: { required, maxLength: maxLength(80) },
        code: { required, maxLength: maxLength(40), format: startAlphanumericDashUnderscore },
    },
    promotion,
    { $autoDirty: true }
);

onMountedPromotion(() => {
    if (props.promotionId) {
        // reset validation to disable submit button after loading promotion
        validate.value.$reset();
    }
});

const submit = async () => {
    const isValid = await validate.value.$validate();
    success.value = false;

    if (!isValid) {
        toasted.error('Please check form data.');
        return;
    }

    try {
        const response = await save();

        if (props.promotionId) {
            success.value = true;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.location = `/settings/integrations/promotions/${response.id}/coupons`;
        }
    } catch (e) {
        console.error(e);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
};
</script>
